'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  className?: string;
}

const testimonials = [
  {
    metric: "312% ROI",
    company: "TechVenture Inc.",
    quote: "Red Letter X transformed our sales pipeline with AI automation that delivered measurable results in 90 days.",
    author: "Sarah Chen, CEO"
  },
  {
    metric: "47% Cost Reduction",
    company: "GlobalRetail Corp",
    quote: "Customer support automation reduced our operational costs while improving response times by 65%.",
    author: "Michael Rodriguez, COO"
  },
  {
    metric: "5x Lead Generation",
    company: "MarketPro Solutions",
    quote: "Marketing automation systems increased our qualified leads by 500% in the first quarter.",
    author: "Jennifer Park, CMO"
  }
];

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handlePrevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <section className={`relative bg-gradient-to-br from-primary via-primary/95 to-secondary overflow-hidden ${className}`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-success rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-2 bg-accent/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <Icon name="SparklesIcon" size={20} className="text-accent" variant="solid" />
              <span className="text-sm font-semibold text-white">AI That Delivers Measurable Results</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight">
              Transform Your Business with Strategic AI Automation
            </h1>

            <p className="text-lg sm:text-xl text-white/90 leading-relaxed">
              Your growth partner, not just a vendor. We deliver strategic systems that drive revenue, optimize operations, and scale your business through intelligent automation.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-accent text-white font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-0.5"
              >
                Schedule Free Consultation
                <Icon name="ArrowRightIcon" size={20} className="ml-2" />
              </Link>
              <Link
                href="/how-it-works"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-heading font-semibold rounded-lg hover:bg-white/20 transition-all duration-300 border border-white/30"
              >
                See How It Works
                <Icon name="PlayIcon" size={20} className="ml-2" variant="solid" />
              </Link>
            </div>

            {/* Trust Signals */}
            <div className="flex flex-wrap items-center gap-6 pt-4">
              <div className="flex items-center space-x-2">
                <Icon name="CheckBadgeIcon" size={24} className="text-success" variant="solid" />
                <span className="text-sm text-white/80">90-Day ROI Guarantee</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="ShieldCheckIcon" size={24} className="text-success" variant="solid" />
                <span className="text-sm text-white/80">Enterprise Security</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="StarIcon" size={24} className="text-success" variant="solid" />
                <span className="text-sm text-white/80">98% Client Satisfaction</span>
              </div>
            </div>
          </div>

          {/* Right Content - Testimonial Carousel */}
          <div className="relative">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 shadow-elevated">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Icon name="ChartBarIcon" size={24} className="text-accent" variant="solid" />
                  <span className="text-2xl font-heading font-bold text-white">
                    {testimonials[currentTestimonial].metric}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePrevTestimonial}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    aria-label="Previous testimonial"
                    suppressHydrationWarning
                  >
                    <Icon name="ChevronLeftIcon" size={20} className="text-white" />
                  </button>
                  <button
                    onClick={handleNextTestimonial}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    aria-label="Next testimonial"
                    suppressHydrationWarning
                  >
                    <Icon name="ChevronRightIcon" size={20} className="text-white" />
                  </button>
                </div>
              </div>

              <blockquote className="text-white/90 text-lg leading-relaxed mb-6">
                "{testimonials[currentTestimonial].quote}"
              </blockquote>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold">{testimonials[currentTestimonial].author}</p>
                  <p className="text-white/70 text-sm">{testimonials[currentTestimonial].company}</p>
                </div>
                <div className="flex space-x-1">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentTestimonial(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentTestimonial ? 'bg-accent w-8' : 'bg-white/30'
                      }`}
                      aria-label={`Go to testimonial ${index + 1}`}
                      suppressHydrationWarning
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;